import { FontAwesome } from '@expo/vector-icons';
import { fontSizes } from '_/config/theme';
import Button from '_/space-management/components/Button';
import Text from '_/space-management/components/Text';
import TextArea from '_/space-management/components/TextArea';
import { colors, spacing } from '_/space-management/constants/theme';
import spaceEvaluationsApi, {
  SpaceEvaluationModel,
} from '_/space-management/services/api/space-evaluations';
import { EventsResponse } from '_/space-management/services/models/events.model';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dimensions, StyleSheet, TouchableOpacity, View } from 'react-native';
import Modal from 'react-native-modal';

interface SpaceEvaluationProps {
  event: EventsResponse;
}

const windowWidth = Dimensions.get('window').width;

export default function SpaceEvaluation({ event }: SpaceEvaluationProps) {
  const { t } = useTranslation();
  // const [evaluation, setEvaluation] = useState<SpaceEvaluationModel | null>(null);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [currentRating, setCurrentRating] = useState(0);
  const [comment, setComment] = useState('');

  useEffect(() => {
    async function loadEvaluation() {
      try {
        const response = await spaceEvaluationsApi.getByEventId(event.id);
        if (response && response.data && response.data.length > 0) {
          // setEvaluation(response.data[0]);
          setCurrentRating(response.data[0].rating);
          setComment(response.data[0].comment || '');
        }
      } catch (error) {
        console.log('Error loading evaluation', error);
      }
    }
    loadEvaluation();
  }, [event.id]);

  const handleStarPress = (rating: number) => {
    setCurrentRating(rating);
    setModalVisible(true);
  };

  const handleSaveEvaluation = async () => {
    setLoading(true);
    try {
      const payload: Partial<SpaceEvaluationModel> = {
        eventId: event.id,
        spaceId: event.slot?.spaceId || '',
        rating: currentRating,
        comment,
      };

      const response = await spaceEvaluationsApi.insert(payload);
      // if (response) {
      //   setEvaluation(response as SpaceEvaluationModel);
      // }
      setModalVisible(false);
    } catch (error) {
      console.log('Error saving evaluation', error);
    } finally {
      setLoading(false);
    }
  };

  const stars = [1, 2, 3, 4, 5];

  return (
    <View style={styles.container}>
      <View style={styles.starsContainer}>
        {stars.map((star) => (
          <TouchableOpacity
            key={star}
            onPress={() => handleStarPress(star)}
            style={styles.starButton}
            hitSlop={{ top: 10, bottom: 10, left: 5, right: 5 }}
          >
            <FontAwesome
              name={star <= currentRating ? 'star' : 'star-o'}
              size={24}
              color={star <= currentRating ? colors.yellow : colors.lightGrey}
            />
          </TouchableOpacity>
        ))}
      </View>

      <Modal
        animationIn="slideInUp"
        isVisible={modalVisible}
        hasBackdrop
        backdropOpacity={0.5}
        onBackdropPress={() => setModalVisible(false)}
        onBackButtonPress={() => setModalVisible(false)}
      >
        <View style={styles.centeredView}>
          <View style={styles.modal}>
            <View style={styles.header}>
              <Text fontSize={fontSizes.xl} color={colors.darkGrey} fontType="bold">
                {t('spaceManage.rateSpace') || 'Avaliar espaço'}
              </Text>
            </View>

            <View style={styles.modalStarsContainer}>
              {stars.map((star) => (
                <TouchableOpacity
                  key={star}
                  onPress={() => setCurrentRating(star)}
                  style={styles.modalStarButton}
                >
                  <FontAwesome
                    name={star <= currentRating ? 'star' : 'star-o'}
                    size={36}
                    color={star <= currentRating ? colors.yellow : colors.lightGrey}
                  />
                </TouchableOpacity>
              ))}
            </View>

            <TextArea
              tag={t('spaceManage.comments') || 'Comentários'}
              value={comment}
              onChangeText={setComment}
              placeholder={t('spaceManage.evaluationCommentPlaceholder') || 'Deixe um comentário (opcional)'}
              maxLength={255}
            />

            <View style={styles.itemsRow}>
              <Button
                onPress={() => setModalVisible(false)}
                backgroundColor={colors.darkBlue}
                style={[styles.modalButton, styles.leftButton]}
                disabled={loading}
              >
                <Text fontSize={fontSizes.lg} color={colors.clear} fontType="bold">
                  {t('spaceManage.cancel') || 'Cancelar'}
                </Text>
              </Button>
              <Button
                backgroundColor={colors.successGreen}
                style={styles.modalButton}
                onPress={handleSaveEvaluation}
                disabled={loading || currentRating === 0}
              >
                <Text fontSize={fontSizes.lg} color={colors.clear} fontType="bold">
                  {t('spaceManage.save') || 'Salvar'}
                </Text>
              </Button>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    marginTop: spacing.md,
    alignItems: 'flex-start',
  },
  starsContainer: {
    flexDirection: 'row',
  },
  starButton: {
    marginRight: spacing.xs,
  },
  centeredView: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  modal: {
    width: windowWidth - 50,
    maxWidth: 768 - 50,
    backgroundColor: colors.clear,
    borderRadius: 16,
    padding: spacing.xl,
  },
  header: {
    alignItems: 'center',
    marginBottom: spacing.lg,
  },
  modalStarsContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    marginBottom: spacing.lg,
  },
  modalStarButton: {
    marginHorizontal: spacing.xs,
  },
  itemsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: spacing.lg,
  },
  modalButton: {
    flex: 1,
    height: 48,
    justifyContent: 'center',
  },
  leftButton: {
    marginRight: spacing.xs,
  },
});
